import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import cn from "classnames";
import { Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { SwipeTracking } from "@/src/component/partial/SwipeTracking";
import { FrontendTrackingService } from "@/src/service";
import { StrapiComponentType, ComponentType } from "@/src/type";

import { SectionModule, TextBreakpointModule } from "@/src/component/partial/__module__";
import CardScrollButtons from "./CardScrollButtons";
import CardScrollSlide from "./CardScrollSlide";

import styles from "./CardScroll.module.scss";

const breakpoints = {
  1025: {
    slidesPerView: 3,
  },
  200: {
    slidesPerView: 1.2,
  },
};

interface CardScrollProps extends ComponentType.DynamicPageSectionComponentProps<StrapiComponentType.Section.CardScroll> {}

function CardScroll({
  section,
  slug,
  snapToGrid,
  lazy,
  classes,
  searchParams,
  quality,
  mobileHint,
  userAgent,
  deviceWidthHint,
  layout,
}: CardScrollProps) {
  const carousel: StrapiComponentType.Section.CardScroll = section.type?.[0];
  const id = `CardScroll-${carousel.id}`;
  const pagination = `card-scroll-${carousel.id}`;
  const prevButton = `card-scroll-prev_${id}`;
  const nextButton = `card-scroll-next_${id}`;

  //this allows the cards to always be centered on mobile in loop ->
  //with a equal portion of another card on each side
  const cards = (carousel?.cards ?? []).length <= 3 ? [...carousel?.cards, ...carousel?.cards] : carousel?.cards;

  return (
    <SectionModule
      section={section}
      slug={slug}
      classes={{
        section: classes?.section,
        children: cn(snapToGrid ? styles.snapToGrid : undefined, styles.sectionChildren),
        styledContainer: cn(snapToGrid ? styles.snapToGrid : undefined, styles.container),
      }}
      stylePrefix="CardScroll"
      styleContainerId={carousel.id}
      snapToGrid={snapToGrid}
      searchParams={searchParams}
      quality={quality}
      layout={layout}
      lazy={lazy}
    >
      <SwipeTracking
        selector={`#swiper_${id}`}
        onSwipe={() => {
          FrontendTrackingService.postTrackingLog({
            action: "swiped",
            actionId: `CardScroll:${carousel.id}`,
            actionIdType: "componentName:componentId",
            actionMessage: `CardScroll section swiped with ${section.name}`,
            triggeredFrom: "CardScroll.tsx:(SwipeTracking.tsx(onSwipe))",
          });
        }}
      />
      {(carousel?.cards ?? []).length > 3 && (
        <CardScrollButtons
          prevClassName={prevButton}
          nextClassName={nextButton}
          classes={{
            prevClassName: styles.buttonPrev,
            nextClassName: styles.buttonNext,
          }}
          tracking={{
            action: "buttonClicked",
            actionId: `CardScroll:${carousel.id}`,
            actionIdType: "componentName:componentId",
            actionMessage: `CardScroll section swiped with ${section.name}`,
            triggeredFrom: "CardScroll.tsx:(CardScrollButtons.tsx(tracking))",
          }}
        />
      )}
      <TextBreakpointModule
        id={`card_scroll_title_${carousel.id}`}
        text={section.title}
        className={styles.title}
      />
      <Swiper
        id={`swiper_${id}`}
        className={styles.swiper}
        wrapperClass={styles.wrapper}
        modules={[Navigation, Pagination]}
        centerInsufficientSlides={true}
        centeredSlides={true}
        centeredSlidesBounds={true}
        spaceBetween={0}
        slidesPerView={3}
        breakpoints={breakpoints}
        //this causing re-sizing issues
        //loop={isMobile || (carousel?.cards ?? []).length > 3}
        loop={true}
        direction="horizontal"
        navigation={{
          prevEl: `#${prevButton}`,
          nextEl: `#${nextButton}`,
        }}
        userAgent={userAgent}
      >
        {(cards ?? []).map((card, i) => (
          <SwiperSlide
            key={`${card.id}_${i}`}
            className={cn(styles.card)}
          >
            <CardScrollSlide
              card={card}
              quality={quality}
              lazy={lazy}
              index={i}
              mobileHint={mobileHint}
              total={(cards ?? []).length}
              deviceWidthHint={deviceWidthHint}
            />
          </SwiperSlide>
        ))}
        <div className={cn(pagination, styles.pagination)}></div>
      </Swiper>
    </SectionModule>
  );
}

export default CardScroll;
